// @ts-strict-ignore
import { SetStateAction } from 'react'
import isEmpty from 'lodash-es/isEmpty'
import { formatDob, logSentryErrorAndGetEventId } from '@node-space/utils'
import { BaseErrorResponse } from 'types/beneficiaries'
import {
  Contact,
  ContactEntity,
  ContactEntityCompany,
  ContactEntityIndividual,
  ContactInstrument,
  ContactPayload,
  RelationshipType,
} from 'types/contacts'
import { ErrorCodes } from 'types/errorCodes'
import { MappedErrorMessageState } from 'types/errors'
import { ComplianceDetails, PartyDetailsType } from 'types/payments'
import { ContactInstrumentFormData } from '../schemas/contactInstrumentSchema'
import { ContactFormData } from '../schemas/contactSchema'

/**
 * Checks if the contact entity is a company.
 * @param {ContactEntityCompany | ContactEntityIndividual} contactEntity - The contact entity to check.
 * @returns {contact is ContactEntityCompany} - True if the contactEntity is a company.
 */
export const isContactEntityCompany = (
  contactEntity: ContactEntityCompany | ContactEntityIndividual
): contactEntity is ContactEntityCompany =>
  !!contactEntity && 'type' in contactEntity && contactEntity?.type === ContactEntity.COMPANY

/**
 * Checks if the contact entity is an individual.
 * @param {ContactEntityCompany | ContactEntityIndividual} contactEntity - The contact entity to check.
 * @returns {contact is ContactEntityIndividual} - True if the contactEntity is an individual.
 */
export const isContactEntityIndividual = (
  contactEntity: ContactEntityCompany | ContactEntityIndividual
): contactEntity is ContactEntityIndividual =>
  !!contactEntity && 'type' in contactEntity && contactEntity?.type === ContactEntity.INDIVIDUAL

/**
 * Gets default values for contact form.
 * @param {boolean} isEditingContact - Whether the contact is being edited.
 * @param {Contact} contact - The contact potentially being edited. Can be null.
 * @param {function(string): string} formatDateData - Format date function.
 * @returns {contact is ContactFormData}
 */

export const getContactDefaultValues = (
  isEditingContact: boolean,
  contact: Contact,
  formatDateData: (value: string) => string
): ContactFormData => {
  const { entity } = contact || ({} as Contact)
  const isCompany = isContactEntityCompany(entity)
  const isIndividual = isContactEntityIndividual(entity)
  const address = entity?.address

  const getIsSelfOwnedDefault = () => {
    if (isEditingContact && contact?.relationshipType === RelationshipType.SELF_OWNED) {
      return true
    } else {
      false
    }
  }

  return {
    entityType: isEditingContact ? entity?.type : ContactEntity?.COMPANY,
    companyName: isEditingContact ? contact?.name : '',
    contactDescription: isEditingContact ? contact?.description : '',
    companyRegistrationNumber: isCompany ? entity?.registrationNumber || '' : '',
    addressLine1: isEditingContact ? address?.addressLine1 || '' : '',
    addressLine2: isEditingContact ? address?.addressLine2 || '' : '',
    city: isEditingContact ? address?.city || '' : '',
    postCode: isEditingContact ? address?.postCode || '' : '',
    region: isEditingContact ? address?.region || '' : '',
    regionCode: isEditingContact ? address?.region || '' : '',
    firstName: isIndividual ? entity?.firstName : '',
    lastName: isIndividual ? entity?.lastName : '',
    countryOfResidence: isEditingContact ? address?.country || '' : '',
    companyRegistrationCountry: isEditingContact ? address?.country || '' : '',
    dateOfBirth: isIndividual ? formatDateData(entity?.dateOfBirth) || '' : '',
    isSelfOwned: getIsSelfOwnedDefault(),
  }
}

/**
 * Gets default values for contact instrument form.
 * @param {Contact} contactInstrument - The contact instrument potentially being edited. Can be null.
 * @returns {contact is ContactInstrumentFormData}
 */
export const getContactInstrumentDefaultValues = (
  contactInstrument: ContactInstrument
): ContactInstrumentFormData => {
  const { description, currency, network, address, tag, selfHosted } =
    contactInstrument || ({} as ContactInstrument)

  return {
    description: description ?? '',
    currencyAndNetwork: currency && network ? `${currency} (${network})` : '',
    walletAddress: address ?? '',
    tag: tag ?? '',
    otp: '',
    selfHosted: selfHosted,
  }
}

/**
 * Gets compliance details object from contact.
 * @param {Contact} contact - Contact used to derive compliance details.
 * @param {PartyDetailsType} type - Type of the compliance party.
 * @param {ContactInstrument} contactInstrument - Contact instrument used to derive paymentInstrumentId.
 * @returns {ComplianceDetails}
 */
export const getContactComplianceDetails = (
  contact: Contact,
  type: PartyDetailsType,
  contactInstrument?: ContactInstrument
): ComplianceDetails => {
  const entity = contact?.entity
  const isCompany = isContactEntityCompany(entity)
  const isIndividual = isContactEntityIndividual(entity)

  const dateValues = isIndividual && entity?.dateOfBirth?.split('-')

  return {
    contactId: contact?.id,
    ...(!isEmpty(contactInstrument) && {
      paymentInstrumentId: contactInstrument?.id,
    }),
    partyDetails: [
      {
        type,
        ...(isCompany && {
          legalName: contact?.name,
          registrationNumber: entity?.registrationNumber,
        }),
        ...(isIndividual && {
          firstName: entity?.firstName,
          lastName: entity?.lastName,
          dateOfBirth: formatDob(dateValues?.[0], dateValues?.[1], dateValues?.[2]),
        }),
        entityType: entity?.type,
        countryCode: entity?.address?.country,
        regionCode: entity?.address?.region,
        relationshipType: contact?.relationshipType,
      },
    ],
  }
}

/**
 * Strip special characters from string but retain letters, numbers, and spaces.
 * @param {string} value - Value being stripped of special characters.
 * @returns {string}
 */
export const stripSpecialCharacters = (value: string) => {
  return value?.replace(/[^a-zA-Z0-9 ]/g, '')
}

/**
 * Handles error events for contact mutations.
 * @param {BaseErrorResponse} error - Request error object.
 * @param {function(SetStateAction<MappedErrorMessageState>): void} setRequestError - Set error function.
 * @param {ContactPayload} data - Request payload.
 * @param {string} mutation - Name of mutation.
 * @param {function(string): string} t - Translation function.
 */
export const handleContactOnError = (
  error: BaseErrorResponse,
  setRequestError: (value: SetStateAction<MappedErrorMessageState>) => void,
  data: ContactPayload,
  mutation: string,
  t: (key: string) => string
) => {
  const errorDetails = error?.data?.details?.errors ?? null
  const errorStatus = error?.status
  let errorMessage = error?.data?.message
  let sentryEventId = null

  if (errorStatus !== 400) {
    sentryEventId = logSentryErrorAndGetEventId(
      `Failed API request to /platform/v1/contacts with status ${error?.status} - ${mutation}`,
      error,
      {
        data: typeof data === 'string' ? { message: data } : data,
      }
    )
  }
  if (error?.data?.code === ErrorCodes.CONTACT_ALREADY_EXISTS) {
    errorMessage = t('errorMessages.contactAlreadyExists')
  }
  setRequestError({
    show: true,
    errorCode: error.status,
    sentryTraceId: sentryEventId,
    handledMessage: errorMessage,
    ErrorMessages: errorDetails,
  })
}
